import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipToContent from "@/components/SkipToContent";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Home, Search, Mic2, Users } from "lucide-react";

const NotFound = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { localizedPath } = useLocalizedPath();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  const quickLinks = [
    { 
      to: "/", 
      icon: Home, 
      label: t("notFoundPage.linkHome", "Home"),
      description: t("notFoundPage.linkHomeDesc", "Back to the main stage")
    },
    { 
      to: "/talent", 
      icon: Users, 
      label: t("notFoundPage.linkTalent", "Our Talent"),
      description: t("notFoundPage.linkTalentDesc", "Browse our voice artists")
    },
    { 
      to: "/services", 
      icon: Mic2, 
      label: t("notFoundPage.linkServices", "Services"),
      description: t("notFoundPage.linkServicesDesc", "What we offer")
    },
    { 
      to: "/contact", 
      icon: Search, 
      label: t("notFoundPage.linkContact", "Contact"),
      description: t("notFoundPage.linkContactDesc", "Get in touch with us")
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SkipToContent />
      <Header />
      <main id="main-content" tabIndex={-1} className="outline-none flex-1 flex items-center justify-center py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          {/* Animated 404 Number */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="relative mb-8"
          >
            <span className="text-[10rem] sm:text-[14rem] font-display font-bold text-primary/10 leading-none select-none">
              404
            </span>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <Mic2 className="w-16 h-16 sm:w-24 sm:h-24 text-primary" strokeWidth={1.5} />
            </motion.div>
          </motion.div>

          {/* Title and Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <h1 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              {t("notFoundPage.title", "Page Not Found")}
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
              {t("notFoundPage.description", "Looks like this page missed its cue. Let's get you back on stage.")}
            </p>
          </motion.div>

          {/* Primary CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mb-12"
          >
            <Button asChild variant="sectionCta" size="lg">
              <Link to={localizedPath("/")}>
                <Home className="w-4 h-4 mr-2" />
                {t("notFoundPage.backHome", "Back to Home")}
              </Link>
            </Button>
          </motion.div>

          {/* Quick Links Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <p className="text-sm text-muted-foreground mb-6 uppercase tracking-widest">
              {t("notFoundPage.quickLinks", "Quick Links")}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {quickLinks.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + index * 0.1, duration: 0.4 }}
                  className="h-full"
                >
                  <Link
                    to={localizedPath(link.to)}
                    className="group flex flex-col items-center justify-center p-4 rounded-lg border border-border bg-card hover:border-primary hover:bg-accent/50 transition-all duration-300 h-full min-h-[120px]"
                  >
                    <link.icon className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors mb-2" />
                    <span className="font-medium text-foreground group-hover:text-primary transition-colors text-sm text-center">
                      {link.label}
                    </span>
                    <span className="text-xs text-muted-foreground mt-1 hidden sm:block text-center">
                      {link.description}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NotFound;
